import { useCallback, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/services/api";
import { usePublicStream } from "@/hooks/usePublicStream";
import type {
  LiveStreamDisplayStatus,
  RecordingPlaybackStatus,
  SessionRecordingSummary,
} from "@/types";

/**
 * Page-level state for the public /watch/:sessionId page. Priority order
 * (first match wins):
 *  - `unavailable`: the session query 404'd (anonymous reads return 404 for
 *    both a missing and an unreadable session — see
 *    assertSessionReadAccessAnonymous on the backend), so nothing else on
 *    the page can be trusted either.
 *  - `live`: an active (or starting/stopping) broadcast always wins over
 *    any recorded video.
 *  - `vod`: at least one recording has a playable (ready|partial) chapter.
 *  - `processing`: recordings exist but none are playable yet.
 *  - `ended`: the broadcast finished (ended/deleted) and left no playable
 *    recording behind — a more specific message than the generic `empty`
 *    "not live right now" card.
 *  - `empty`: no live stream and no recordings at all.
 */
export type PageState =
  | "unavailable"
  | "live"
  | "vod"
  | "processing"
  | "ended"
  | "empty";

// One recording as returned by GET /sessions/:id/recordings. Kept as an
// alias (rather than a hand-picked subset) so the player components built on
// top of this hook (WatchPlayer, AngleSwitcher) can read whichever recording
// fields they need without this hook growing a second, drifting shape.
export type Fragment = SessionRecordingSummary;

/**
 * Recordings sharing an `angleName` are fragments of the same camera angle
 * (a dropped LTE connection splits one angle into multiple non-consecutive
 * recordings). `name` is that shared `angleName`, or `null` when the group
 * is a single-fragment fallback group keyed by the recording's own id
 * (i.e. the recording has no angleName).
 */
export interface AngleGroup {
  name: string | null;
  fragments: Fragment[];
  firstPlayableFragment: string | null;
}

// The recordings query polls while any recording is still transcoding, or
// while the live stream is active/transitioning — a broadcast that ends on
// a session with no recordings yet (or none uploaded so far) must not leave
// this query permanently stopped, or the VOD handoff after the stream ends
// would need a page reload to surface. It stops only once every recording
// has settled into a terminal playbackStatus (ready or unavailable) AND the
// stream is not live.
const RECORDINGS_POLL_INTERVAL_MS = 30_000;

// Mirrors WatchPage's existing ACTIVE_STATUSES treatment of "actively
// streaming, or transitioning into/out of it" — a broadcast in any of these
// states (with a whepUrl) takes over the page from recorded video.
const LIVE_DISPLAY_STATUSES = new Set<LiveStreamDisplayStatus>([
  "live",
  "starting",
  "stopping",
]);

// Terminal broadcast states. A just-ended stream that left no recordings
// behind gets a dedicated "Stream has ended" card rather than the generic
// "Not live right now" one, since the viewer knows (and expects feedback
// that) something was just live.
const ENDED_DISPLAY_STATUSES = new Set<LiveStreamDisplayStatus>([
  "ended",
  "deleted",
]);

const PLAYABLE_STATUSES = new Set<RecordingPlaybackStatus>(["ready", "partial"]);
const POLLABLE_STATUSES = new Set<RecordingPlaybackStatus>(["pending", "partial"]);

function isPlayable(status: RecordingPlaybackStatus): boolean {
  return PLAYABLE_STATUSES.has(status);
}

// Exported (rather than inlined in the query's `refetchInterval` option) so
// it can be unit-tested directly against its four input combinations —
// mirrors useLiveStream's `getLiveStreamPollInterval`.
export function getRecordingsPollInterval(
  recordings: SessionRecordingSummary[],
  streamDisplayStatus: LiveStreamDisplayStatus | undefined,
): number | false {
  const stillProcessing = recordings.some((recording) =>
    POLLABLE_STATUSES.has(recording.playbackStatus),
  );
  const streamIsActive = Boolean(
    streamDisplayStatus && LIVE_DISPLAY_STATUSES.has(streamDisplayStatus),
  );
  return stillProcessing || streamIsActive ? RECORDINGS_POLL_INTERVAL_MS : false;
}

// Anonymous reads 404 for both a missing and an unreadable session/recording
// set; either way there is nothing to gain by retrying.
function retryExceptNotFound(failureCount: number, error: unknown): boolean {
  const axiosError = error as { response?: { status?: number } };
  if (axiosError?.response?.status === 404) return false;
  return failureCount < 2;
}

// Nulls sort last; equal (including both-null) timestamps compare equal so
// the sort is stable and preserves the original (API) ordering for ties.
function compareByRecordedAt(a: Fragment, b: Fragment): number {
  if (a.recordedAt === null && b.recordedAt === null) return 0;
  if (a.recordedAt === null) return 1;
  if (b.recordedAt === null) return -1;
  return new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime();
}

// Exported so owner-facing compositions (SessionVideosSection on the session
// detail page) can group the same session's recordings into angles without
// duplicating this logic — the public /watch page and the detail page must
// agree on what counts as "one angle."
export function buildAngleGroups(recordings: SessionRecordingSummary[]): AngleGroup[] {
  const order: string[] = [];
  const byKey = new Map<string, Fragment[]>();

  for (const recording of recordings) {
    const key = recording.angleName ?? recording.id;
    const bucket = byKey.get(key);
    if (bucket) {
      bucket.push(recording);
    } else {
      byKey.set(key, [recording]);
      order.push(key);
    }
  }

  const groups = order.map((key): AngleGroup => {
    const fragments = [...byKey.get(key)!].sort(compareByRecordedAt);
    const firstPlayable = fragments.find((fragment) =>
      isPlayable(fragment.playbackStatus),
    );
    return {
      name: fragments[0]?.angleName ?? null,
      fragments,
      firstPlayableFragment: firstPlayable?.id ?? null,
    };
  });

  return groups.sort((a, b) =>
    compareByRecordedAt(a.fragments[0] as Fragment, b.fragments[0] as Fragment),
  );
}

/**
 * Data layer for the public /watch/:sessionId page. Fetches the session
 * (title/visibility), the live stream status (via `usePublicStream`), and
 * the session's recordings — all anonymous-capable — and derives the page
 * state and angle groupings the watch player components render from.
 */
export function useSessionWatchData(sessionId: string) {
  const sessionQuery = useQuery({
    queryKey: ["sessions", sessionId],
    queryFn: () => apiClient.getSession(sessionId),
    enabled: !!sessionId,
    retry: retryExceptNotFound,
  });

  const streamQuery = usePublicStream(sessionId);

  const recordingsQuery = useQuery({
    queryKey: ["sessions", sessionId, "recordings"],
    queryFn: () => apiClient.getSessionRecordings(sessionId),
    enabled: !!sessionId,
    retry: retryExceptNotFound,
    refetchInterval: (query) =>
      getRecordingsPollInterval(
        query.state.data?.recordings ?? [],
        streamQuery.data?.displayStatus,
      ),
  });

  const angleGroups = useMemo(
    () => buildAngleGroups(recordingsQuery.data?.recordings ?? []),
    [recordingsQuery.data],
  );

  const pageState: PageState = useMemo(() => {
    if (sessionQuery.isError) return "unavailable";

    const displayStatus = streamQuery.data?.displayStatus;
    if (
      displayStatus &&
      LIVE_DISPLAY_STATUSES.has(displayStatus) &&
      streamQuery.data?.whepUrl
    ) {
      return "live";
    }

    const recordings = recordingsQuery.data?.recordings ?? [];
    if (recordings.some((recording) => isPlayable(recording.playbackStatus))) {
      return "vod";
    }
    if (recordings.length > 0) return "processing";
    if (displayStatus && ENDED_DISPLAY_STATUSES.has(displayStatus)) {
      return "ended";
    }
    return "empty";
  }, [sessionQuery.isError, streamQuery.data, recordingsQuery.data]);

  // Resolves a recording id to a playable fragment. Given no id (or an id
  // that is missing/not currently playable), falls back to the first
  // playable fragment of the first angle group that has one — mirroring the
  // design's `?r=` fallback for an invalid or unplayable selection.
  const getPlayableFragment = useCallback(
    (recordingId?: string): Fragment | undefined => {
      if (recordingId) {
        for (const group of angleGroups) {
          const fragment = group.fragments.find((f) => f.id === recordingId);
          if (fragment && isPlayable(fragment.playbackStatus)) {
            return fragment;
          }
        }
      }
      for (const group of angleGroups) {
        if (group.firstPlayableFragment) {
          return group.fragments.find(
            (f) => f.id === group.firstPlayableFragment,
          );
        }
      }
      return undefined;
    },
    [angleGroups],
  );

  return {
    session: sessionQuery.data,
    pageState,
    angleGroups,
    streamData: streamQuery.data,
    refetchRecordings: recordingsQuery.refetch,
    getPlayableFragment,
    isLoading:
      sessionQuery.isLoading || recordingsQuery.isLoading || streamQuery.isLoading,
  };
}
